import type { ComponentType } from 'react';
import type {
  ActivityActorResponse,
  ActivityActorsResponse,
  ActivityEventResponse,
  ActivityListResponse
} from 'we-tree-api-client/v2';
import BookOpen from '@/icons/BookOpen';
import FileText from '@/icons/FileText';
import History from '@/icons/History';
import Image from '@/icons/Image';
import Mail from '@/icons/Mail';
import PawPrint from '@/icons/PawPrint';
import Pencil from '@/icons/Pencil';
import TreeDeciduous from '@/icons/TreeDeciduous';
import Upload from '@/icons/Upload';
import User from '@/icons/User';
import Users from '@/icons/Users';
import { getActivityCategoryLabel, getActivityTargetTypeLabel } from '@/i18n/labelMaps';

export type ActivityVisibility = 'family' | 'actor_only';

export type ActivityCategory = 'all' | 'people' | 'media' | 'invites' | 'imports' | 'tree' | 'memories';

export type ActivitySortOrder = 'asc' | 'desc';

/** API activity row; alias of generated client type. */
export type ActivityEvent = ActivityEventResponse;

export type { ActivityActorsResponse, ActivityListResponse };

export interface FamilyActivitySearchParams {
  page?: number;
  pageSize?: number;
  sortOrder?: ActivitySortOrder;
  createdAfter?: string;
  createdBefore?: string;
  userId?: string;
  targetType?: string;
  action?: string;
  category?: Exclude<ActivityCategory, 'all'>;
}

export interface FamilyActivityActorSearchParams {
  createdAfter?: string;
  createdBefore?: string;
  targetType?: string;
  action?: string;
  category?: Exclude<ActivityCategory, 'all'>;
}

/** Alias of generated client type for activity filter actors. */
export type ActivityActor = ActivityActorResponse;

export interface AccountActivitySearchParams {
  page?: number;
  pageSize?: number;
  sortOrder?: ActivitySortOrder;
  createdAfter?: string;
  createdBefore?: string;
}

const ACTIVITY_CATEGORY_IDS = [
  'all',
  'people',
  'memories',
  'media',
  'invites',
  'tree',
  'imports'
] as const satisfies readonly ActivityCategory[];

export const ACTIVITY_CATEGORIES: { id: ActivityCategory; label: string }[] = ACTIVITY_CATEGORY_IDS.map((id) => ({
  id,
  get label() {
    return getActivityCategoryLabel(id);
  }
}));

const ACTIVITY_TARGET_TYPE_VALUES = [
  'family_member',
  'life_event',
  'person_fact',
  'person',
  'pet',
  'story',
  'media_asset',
  'media_export_job',
  'gedcom_export',
  'invitation',
  'user',
  'relationship',
  'import_job',
  'family',
  'source',
  'citation'
] as const;

export const ACTIVITY_TARGET_TYPES: { value: string; label: string }[] = ACTIVITY_TARGET_TYPE_VALUES.map((value) => ({
  value,
  get label() {
    return getActivityTargetTypeLabel(value);
  }
}));

type ActivityIconMeta = {
  Icon: ComponentType<{ width?: number; height?: number; className?: string }>;
  accentClass: string;
};

const DEFAULT_ICON: ActivityIconMeta = { Icon: History, accentClass: 'activity-item__icon--default' };

const ACTION_ICON_MAP: Record<string, ActivityIconMeta> = {
  person_created: { Icon: User, accentClass: 'activity-item__icon--people' },
  person_updated: { Icon: Pencil, accentClass: 'activity-item__icon--people' },
  family_member_deleted: { Icon: User, accentClass: 'activity-item__icon--people' },
  life_event_created: { Icon: History, accentClass: 'activity-item__icon--people' },
  life_event_updated: { Icon: Pencil, accentClass: 'activity-item__icon--people' },
  life_event_deleted: { Icon: History, accentClass: 'activity-item__icon--people' },
  person_fact_created: { Icon: FileText, accentClass: 'activity-item__icon--people' },
  person_fact_updated: { Icon: Pencil, accentClass: 'activity-item__icon--people' },
  person_fact_deleted: { Icon: FileText, accentClass: 'activity-item__icon--people' },
  pet_created: { Icon: PawPrint, accentClass: 'activity-item__icon--people' },
  pet_updated: { Icon: PawPrint, accentClass: 'activity-item__icon--people' },
  story_created: { Icon: BookOpen, accentClass: 'activity-item__icon--memories' },
  story_updated: { Icon: BookOpen, accentClass: 'activity-item__icon--memories' },
  story_deleted: { Icon: BookOpen, accentClass: 'activity-item__icon--memories' },
  story_approved: { Icon: BookOpen, accentClass: 'activity-item__icon--memories' },
  story_rejected: { Icon: BookOpen, accentClass: 'activity-item__icon--memories' },
  suggestion_created: { Icon: Pencil, accentClass: 'activity-item__icon--people' },
  suggestion_updated: { Icon: Pencil, accentClass: 'activity-item__icon--people' },
  suggestion_cancelled: { Icon: Pencil, accentClass: 'activity-item__icon--people' },
  suggestion_changes_requested: { Icon: Pencil, accentClass: 'activity-item__icon--people' },
  suggestion_rejected: { Icon: Pencil, accentClass: 'activity-item__icon--people' },
  suggestion_approved: { Icon: Pencil, accentClass: 'activity-item__icon--people' },
  media_uploaded: { Icon: Image, accentClass: 'activity-item__icon--media' },
  media_deleted: { Icon: Image, accentClass: 'activity-item__icon--media' },
  media_exported: { Icon: Upload, accentClass: 'activity-item__icon--media' },
  invitation_sent: { Icon: Mail, accentClass: 'activity-item__icon--invites' },
  invitation_accepted: { Icon: Users, accentClass: 'activity-item__icon--invites' },
  invitation_cancelled: { Icon: Mail, accentClass: 'activity-item__icon--invites' },
  family_user_role_changed: { Icon: Users, accentClass: 'activity-item__icon--invites' },
  relationship_created: { Icon: TreeDeciduous, accentClass: 'activity-item__icon--tree' },
  relationship_updated: { Icon: TreeDeciduous, accentClass: 'activity-item__icon--tree' },
  relationship_deleted: { Icon: TreeDeciduous, accentClass: 'activity-item__icon--tree' },
  family_renamed: { Icon: Users, accentClass: 'activity-item__icon--tree' },
  family_owner_transferred: { Icon: Users, accentClass: 'activity-item__icon--tree' },
  import_completed: { Icon: Upload, accentClass: 'activity-item__icon--imports' },
  import_rolled_back: { Icon: Upload, accentClass: 'activity-item__icon--imports' },
  source_created: { Icon: BookOpen, accentClass: 'activity-item__icon--tree' },
  citation_created: { Icon: FileText, accentClass: 'activity-item__icon--tree' },
  user_nickname_updated: { Icon: User, accentClass: 'activity-item__icon--account' },
  user_email_updated: { Icon: Mail, accentClass: 'activity-item__icon--account' },
  family_display_name_set: { Icon: Pencil, accentClass: 'activity-item__icon--account' },
  family_display_name_cleared: { Icon: Pencil, accentClass: 'activity-item__icon--account' }
};

export function getActivityIconMeta(action: string): ActivityIconMeta {
  return ACTION_ICON_MAP[action] ?? DEFAULT_ICON;
}
